import React from 'react';
import { Link } from 'react-router-dom';
import buildingImg from '../assets/building.png';
import SEO from '../components/SEO';

const SignatureTower = () => {
  return (
    <div className="bg-[#041424] min-h-screen w-full font-sans text-white">
      <SEO 
        title="Signature Tower | HBB0011 REALTY"
        description="Discover the Signature Tower by HBB0011 REALTY. An iconic luxury real estate project redefining modern living and corporate workspaces."
        keywords="Signature Tower, luxury real estate, corporate workspaces, HBB0011 REALTY projects"
      />
      
      {/* 1. Hero Section */}
      <section className="relative w-full pt-28 pb-24 md:pt-32 md:pb-32 lg:min-h-[85vh] flex items-center overflow-hidden">
        {/* Background Image Blend */}
        <div className="absolute inset-0 z-0">
          <img 
            alt="Signature Tower Exterior Night View" 
            className="w-full h-full object-cover opacity-30 mix-blend-luminosity" 
            src={buildingImg}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#041424] via-[#041424]/80 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#041424] via-[#041424]/20 to-transparent"></div>
        </div>

        {/* Hero Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
          <div className="max-w-3xl text-left">
            
            {/* Breadcrumbs */}
            <div className="flex items-center text-xs sm:text-sm font-bold tracking-widest mb-6 gap-2">
              <Link to="/" className="text-white hover:text-[#D4AF37] transition-colors uppercase">Home</Link>
              <span className="text-gray-400 material-symbols-outlined text-sm">chevron_right</span>
              <Link to="/projects" className="text-white hover:text-[#D4AF37] transition-colors uppercase">Projects</Link>
              <span className="text-gray-400 material-symbols-outlined text-sm">chevron_right</span>
              <span className="text-[#D4AF37] uppercase">Signature Tower</span>
            </div>

            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-4 leading-tight tracking-tight uppercase">
              Signature<br className="hidden sm:block"/> Tower
            </h1>
            
            <h2 className="text-lg sm:text-xl md:text-2xl text-[#D4AF37] font-medium mb-6 uppercase tracking-wide leading-relaxed">
              A Landmark That Defines<br/>Vision. Trust. Excellence.
            </h2>
            
            <p className="text-sm sm:text-base md:text-lg text-gray-300 mb-10 max-w-xl leading-relaxed">
              Signature Tower is more than a building; it is a statement of excellence, innovation, and timeless architecture.
            </p>

            {/* Key Highlights */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-6 sm:gap-8 mb-10">
              <div className="flex items-center gap-3">
                <span className="material-symbols-outlined text-[#D4AF37] text-3xl sm:text-4xl font-light">workspace_premium</span>
                <div>
                  <div className="text-white font-bold text-lg sm:text-xl">20</div>
                  <div className="text-gray-400 text-[10px] uppercase tracking-widest">Cent Landmark</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="material-symbols-outlined text-[#D4AF37] text-3xl sm:text-4xl font-light">domain</span>
                <div>
                  <div className="text-white font-bold text-sm uppercase">Iconic</div>
                  <div className="text-gray-400 text-[10px] uppercase tracking-widest">Architecture</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="material-symbols-outlined text-[#D4AF37] text-3xl sm:text-4xl font-light">diamond</span>
                <div>
                  <div className="text-white font-bold text-sm uppercase">Premium</div>
                  <div className="text-gray-400 text-[10px] uppercase tracking-widest">Commercial</div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button className="bg-[#D4AF37] text-[#041424] px-6 py-4 rounded-sm font-bold text-xs uppercase tracking-widest hover:bg-white transition-colors flex items-center justify-center gap-2">
                Download Brochure <span className="material-symbols-outlined text-base">download</span>
              </button>
              <button className="border border-white text-white px-6 py-4 rounded-sm font-bold text-xs uppercase tracking-widest hover:bg-white hover:text-[#041424] transition-colors flex items-center justify-center gap-2">
                Book a Site Visit <span className="material-symbols-outlined text-base">event</span>
              </button>
            </div>

          </div>
        </div>
      </section>

      {/* 2. Quick Stats Bar (Overlapping Hero) */}
      <section className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 sm:-mt-16 lg:-mt-20 mb-16 lg:mb-24">
        <div className="bg-[#06121e] border-t border-l border-r border-[#D4AF37]/30 border-t-[#D4AF37] rounded-sm shadow-2xl p-6 md:p-8 lg:p-10">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 divide-x divide-gray-800">
            
            <div className="flex flex-col xl:flex-row items-center justify-center lg:justify-start gap-3 xl:gap-4 text-center xl:text-left px-2">
              <span className="material-symbols-outlined text-[#D4AF37] text-4xl font-light">aspect_ratio</span>
              <div>
                <div className="text-2xl sm:text-3xl font-bold text-white mb-1">2.5+</div>
                <div className="text-[10px] font-bold text-[#D4AF37] tracking-widest uppercase">Lakh Sq. Ft.</div>
                <div className="text-[9px] text-gray-400 uppercase tracking-wider mt-1">Total Development</div>
              </div>
            </div>
            
            <div className="flex flex-col xl:flex-row items-center justify-center lg:justify-start gap-3 xl:gap-4 text-center xl:text-left px-2">
              <span className="material-symbols-outlined text-[#D4AF37] text-4xl font-light">apartment</span>
              <div>
                <div className="text-2xl sm:text-3xl font-bold text-white mb-1">G+7</div>
                <div className="text-[10px] font-bold text-[#D4AF37] tracking-widest uppercase">Floors</div>
                <div className="text-[9px] text-gray-400 uppercase tracking-wider mt-1">Iconic Structure</div>
              </div>
            </div>
            
            <div className="flex flex-col xl:flex-row items-center justify-center lg:justify-start gap-3 xl:gap-4 text-center xl:text-left px-2">
              <span className="material-symbols-outlined text-[#D4AF37] text-4xl font-light">business_center</span>
              <div>
                <div className="text-2xl sm:text-3xl font-bold text-white mb-1">100+</div>
                <div className="text-[10px] font-bold text-[#D4AF37] tracking-widest uppercase">Premium Offices</div>
                <div className="text-[9px] text-gray-400 uppercase tracking-wider mt-1">& Spaces</div>
              </div>
            </div>
            
            <div className="flex flex-col xl:flex-row items-center justify-center lg:justify-start gap-3 xl:gap-4 text-center xl:text-left px-2">
              <span className="material-symbols-outlined text-[#D4AF37] text-4xl font-light">local_parking</span>
              <div>
                <div className="text-2xl sm:text-3xl font-bold text-white mb-1">500+</div>
                <div className="text-[10px] font-bold text-[#D4AF37] tracking-widest uppercase">Parking Capacity</div>
                <div className="text-[9px] text-gray-400 uppercase tracking-wider mt-1">4 Levels</div>
              </div>
            </div>
            
            <div className="flex flex-col xl:flex-row items-center justify-center lg:justify-start gap-3 xl:gap-4 text-center xl:text-left px-2 col-span-2 md:col-span-1 lg:col-span-1 border-l-0 md:border-l md:border-gray-800 lg:border-l">
              <span className="material-symbols-outlined text-[#D4AF37] text-4xl font-light">star</span>
              <div>
                <div className="text-2xl sm:text-3xl font-bold text-white mb-1">100%</div>
                <div className="text-[10px] font-bold text-[#D4AF37] tracking-widest uppercase">Vaastu Compliant</div>
                <div className="text-[9px] text-gray-400 uppercase tracking-wider mt-1">Design</div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16 lg:space-y-24 pb-20">
        
        {/* 3. About / Features Grid */}
        <section>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
            
            {/* Left: Text Content */}
            <div className="lg:col-span-4 flex flex-col justify-center order-2 lg:order-1 bg-[#06121e] border border-gray-800 p-8 rounded-sm">
              <h4 className="text-[#D4AF37] text-[10px] sm:text-xs font-bold tracking-widest uppercase mb-3">About Signature Tower</h4>
              <h2 className="text-3xl sm:text-4xl text-white font-light mb-6 leading-tight">
                Where Architecture <br/>
                <span className="font-bold">Meets Ambition</span>
              </h2>
              
              <p className="text-gray-400 mb-8 leading-relaxed text-sm">
                Signature Tower is a premium commercial landmark designed for businesses that aspire to lead. With iconic architecture, world-class amenities, and a prime location, it offers the perfect environment to grow, connect, and succeed.
              </p>
              
              <ul className="space-y-4 text-gray-300 font-medium text-xs sm:text-sm">
                <li className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-[#D4AF37] text-lg mt-0.5">check_circle</span>
                  <span>Iconic Façade with Modern Design</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-[#D4AF37] text-lg mt-0.5">check_circle</span>
                  <span>Grand Entrance & Double Height Lobby</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-[#D4AF37] text-lg mt-0.5">check_circle</span>
                  <span>High-Speed Elevators</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-[#D4AF37] text-lg mt-0.5">check_circle</span>
                  <span>Advanced Security & Surveillance</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-[#D4AF37] text-lg mt-0.5">check_circle</span>
                  <span>Energy Efficient & Sustainable Building</span>
                </li>
              </ul>
            </div>

            {/* Center: Main Image */}
            <div className="lg:col-span-5 relative rounded-sm overflow-hidden group border border-gray-800 h-[400px] md:h-[500px] lg:h-auto order-1 lg:order-2">
              <img 
                alt="Grand Entrance Lobby" 
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
                src={buildingImg}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#041424] via-[#041424]/20 to-transparent opacity-90"></div>
              
              <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-center w-full">
                <div className="text-white font-bold text-xl sm:text-2xl tracking-widest uppercase">HBB0011</div>
                <div className="text-[#D4AF37] text-[10px] uppercase tracking-widest mt-1">Realty Realtors</div>
              </div>
            </div>

            {/* Right: Sidebar Stacked Images */}
            <div className="lg:col-span-3 flex flex-col gap-4 sm:gap-6 order-3 lg:order-3">
              <div className="relative h-48 sm:h-56 lg:flex-1 w-full rounded-sm overflow-hidden group border border-gray-800">
                <img alt="Landscaped Terrace" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" src={buildingImg}/>
                <div className="absolute inset-0 bg-gradient-to-t from-[#041424]/90 via-[#041424]/20 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4 text-white font-bold text-[10px] sm:text-xs tracking-widest uppercase">
                  Landscaped Terrace
                </div>
              </div>

              <div className="relative h-48 sm:h-56 lg:flex-1 w-full rounded-sm overflow-hidden group border border-gray-800">
                <img alt="Premium Business Spaces" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" src={buildingImg}/>
                <div className="absolute inset-0 bg-gradient-to-t from-[#041424]/90 via-[#041424]/20 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4 text-white font-bold text-[10px] sm:text-xs tracking-widest uppercase">
                  Premium Business Spaces
                </div>
              </div>

              <div className="relative h-48 sm:h-56 lg:flex-1 w-full rounded-sm overflow-hidden group border border-gray-800">
                <img alt="Retail Spaces" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" src={buildingImg}/>
                <div className="absolute inset-0 bg-gradient-to-t from-[#041424]/90 via-[#041424]/20 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4 text-white font-bold text-[10px] sm:text-xs tracking-widest uppercase">
                  Retail Spaces
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* 4. Built For Excellence & Map Section */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          
          {/* Left: Built For Excellence */}
          <div className="lg:col-span-6 bg-[#06121e] border border-gray-800 rounded-sm p-6 sm:p-8">
            <h4 className="text-[#D4AF37] uppercase text-[10px] sm:text-xs font-bold tracking-widest mb-8">Built For Excellence</h4>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              
              <div className="flex items-start gap-4">
                <span className="material-symbols-outlined text-[#D4AF37] text-3xl font-light">location_on</span>
                <div>
                  <h5 className="text-white text-xs font-bold uppercase tracking-widest mb-1">Prime Location</h5>
                  <p className="text-gray-400 text-[10px] leading-relaxed">High visibility & excellent connectivity</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <span className="material-symbols-outlined text-[#D4AF37] text-3xl font-light">architecture</span>
                <div>
                  <h5 className="text-white text-xs font-bold uppercase tracking-widest mb-1">Modern Infrastructure</h5>
                  <p className="text-gray-400 text-[10px] leading-relaxed">State-of-the-art design and construction</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <span className="material-symbols-outlined text-[#D4AF37] text-3xl font-light">eco</span>
                <div>
                  <h5 className="text-white text-xs font-bold uppercase tracking-widest mb-1">Sustainable Design</h5>
                  <p className="text-gray-400 text-[10px] leading-relaxed">Eco-friendly materials & energy efficient</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <span className="material-symbols-outlined text-[#D4AF37] text-3xl font-light">settings_remote</span>
                <div>
                  <h5 className="text-white text-xs font-bold uppercase tracking-widest mb-1">Smart Building</h5>
                  <p className="text-gray-400 text-[10px] leading-relaxed">Advanced automation & management</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <span className="material-symbols-outlined text-[#D4AF37] text-3xl font-light">king_bed</span>
                <div>
                  <h5 className="text-white text-xs font-bold uppercase tracking-widest mb-1">Premium Amenities</h5>
                  <p className="text-gray-400 text-[10px] leading-relaxed">Elevated experience for businesses</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <span className="material-symbols-outlined text-[#D4AF37] text-3xl font-light">trending_up</span>
                <div>
                  <h5 className="text-white text-xs font-bold uppercase tracking-widest mb-1">Future Ready</h5>
                  <p className="text-gray-400 text-[10px] leading-relaxed">Built to adapt and grow with your business</p>
                </div>
              </div>

            </div>
          </div>

          {/* Right: Location & Map */}
          <div className="lg:col-span-6 bg-[#06121e] border border-gray-800 rounded-sm overflow-hidden flex flex-col">
            <div className="p-6 sm:p-8 pb-4">
              <h4 className="text-[#D4AF37] uppercase text-[10px] sm:text-xs font-bold tracking-widest mb-2">Prime Location</h4>
              <h2 className="text-2xl sm:text-3xl text-white font-bold mb-4 uppercase tracking-wide">To Be Announced</h2>
              <p className="text-gray-400 text-xs sm:text-sm leading-relaxed mb-6">
                Location details for this project will be updated soon.
              </p>
            </div>
            
            <div className="relative flex-grow min-h-[300px] w-full bg-[#020b14] border-t border-gray-800 flex items-center justify-center">
              <p className="text-gray-600 text-xs uppercase tracking-widest">No Map Available</p>
            </div>
          </div>

        </section>

      </main>

      {/* 5. CTA Bar */}
      <section className="border-t border-b border-gray-800 bg-[#06121e] py-10 lg:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center justify-between gap-6 lg:gap-8">
          
          <div className="text-center lg:text-left">
            <h3 className="text-2xl sm:text-3xl text-white font-light mb-2">
              Ready to Elevate Your Business?
            </h3>
            <p className="text-gray-400 text-sm">
              Be a part of an iconic landmark that inspires success.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full lg:w-auto">
            <button className="bg-[#D4AF37] text-[#041424] px-6 py-4 rounded-sm font-bold text-[10px] sm:text-xs uppercase tracking-widest hover:bg-white transition-colors flex items-center justify-center gap-2 w-full sm:w-auto">
              Schedule a Site Visit
              <span className="material-symbols-outlined text-sm sm:text-base">event</span>
            </button>
            <button className="border border-white text-white px-6 py-4 rounded-sm font-bold text-[10px] sm:text-xs uppercase tracking-widest hover:bg-white hover:text-[#041424] transition-colors flex items-center justify-center gap-2 w-full sm:w-auto">
              Talk to an Expert
              <span className="material-symbols-outlined text-sm sm:text-base">call</span>
            </button>
            <a href="tel:+919916710011" className="border border-gray-700 text-white px-6 py-4 rounded-sm font-bold text-[10px] sm:text-xs uppercase tracking-widest hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors flex items-center justify-center gap-2 w-full sm:w-auto">
              <span className="material-symbols-outlined text-sm sm:text-base text-[#D4AF37]">phone_in_talk</span>
              +91 99167 10011
            </a>
          </div>

        </div>
      </section>

    </div>
  );
};

export default SignatureTower;